import { useState, useEffect, useCallback } from "react";
import { Bell, Check } from "lucide-react";
import api from "../lib/api";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import { Button } from "./ui/button";

const TYPE_DOT = {
  approval: "bg-emerald-500",
  booking: "bg-blue-600",
  cancellation: "bg-red-500",
  exchange: "bg-amber-500",
  reminder: "bg-indigo-500",
  registration: "bg-slate-400",
  rejection: "bg-red-500",
};

const POLL_INTERVAL_MS = 20000;

export function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.notifications);
      setUnread(data.unread);
    } catch (e) {
      if (process.env.NODE_ENV === "development") console.error("Failed to load notifications", e);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [load]);

  const markAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <PopoverTrigger asChild>
        <button
          data-testid="notification-bell"
          className="relative rounded-full p-2 text-slate-600 transition-colors hover:bg-slate-100 hover:text-blue-700"
          aria-label="Notifiche"
        >
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span
              data-testid="notification-badge"
              className="absolute right-0.5 top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white"
            >
              {unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <h4 className="font-heading text-sm font-700 text-slate-900">Notifiche</h4>
          {unread > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 gap-1 text-xs text-blue-700"
              onClick={markAll}
              data-testid="mark-all-read-btn"
            >
              <Check className="h-3 w-3" /> Segna lette
            </Button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-slate-400">Nessuna notifica</p>
          ) : (
            items.map((n) => (
              <div
                key={n.id}
                className={`flex gap-3 border-b border-slate-100 px-4 py-3 text-sm ${
                  n.read ? "bg-white" : "bg-sky-50"
                }`}
              >
                <span
                  className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${TYPE_DOT[n.type] || "bg-slate-400"}`}
                />
                <div>
                  <p className="text-slate-700">{n.message}</p>
                  <p className="mt-1 text-[11px] text-slate-400">
                    {new Date(n.created_at).toLocaleString("it-IT")}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
